// Shared keyboard helpers for Tally-style forms across the app.
// Originally lived at layouts/pharmacy/inward/quickAdd/kbd.js.

/**
 * onKeyDown handler for a form container (a <DialogContent> or wrapping <Box>):
 *   - Ctrl/Cmd + Enter          → save()
 *   - Enter (plain, single-line) → move focus to the next focusable field
 *   - Enter inside a <textarea>  → left alone (newline)
 *   - Enter on a <button>        → left alone (click)
 *   - Enter while an Autocomplete listbox is open → left alone (option select)
 */
export function formKeyHandler(save) {
  return (e) => {
    if (e.key !== 'Enter') return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      save();
      return;
    }
    if (e.shiftKey) return;
    const el = e.target;
    const tag = (el.tagName || '').toLowerCase();
    if (tag === 'textarea' || tag === 'button') return;
    if (el.getAttribute && el.getAttribute('role') === 'option') return;
    // An open Autocomplete listbox handles Enter itself (select the option).
    if (el.closest && el.closest('[aria-expanded="true"]')) return;
    e.preventDefault();
    focusNext(el, e.currentTarget);
  };
}

/** Focus the next visible, enabled form control after `el` within `root`. */
export function focusNext(el, root) {
  if (!root) return;
  const focusables = Array.from(
    root.querySelectorAll(
      'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), [role="combobox"], button:not([disabled])',
    ),
  ).filter((n) => n.offsetParent !== null && n.tabIndex !== -1);
  const i = focusables.indexOf(el);
  const next = focusables[i + 1];
  if (next) next.focus();
}

/** Focus the first real control inside a ref'd wrapper element (or the element itself). */
export function focusRef(ref) {
  const el = ref && 'current' in ref ? ref.current : ref;
  if (!el) return;
  const target = el.querySelector
    ? (el.querySelector('input:not([type="hidden"]), textarea, [role="combobox"], button') || el)
    : el;
  target.focus();
}

/** Focus the first focusable control inside `root`. */
export function focusFirstIn(root) {
  if (!root) return;
  const first = root.querySelector(
    'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), [role="combobox"], button:not([disabled])',
  );
  if (first) first.focus();
}

/**
 * onKeyDown handler for a header field that advances to the next field on Enter.
 *   next: a ref (focus its first control) OR a function (called on Enter).
 * Skips Shift+Enter and Enter while an Autocomplete/Select popup is open.
 */
export function headerEnterNav(next) {
  return (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    if (e.target?.closest && e.target.closest('[aria-expanded="true"]')) return;
    e.preventDefault();
    if (typeof next === 'function') next();
    else focusRef(next);
  };
}
